// Helpers para construir el deep link al installer (Shopify embedded app)
// donde el usuario completa el flujo de billing.

// Deep link dentro de Shopify Admin a la ruta `/app/pricing` de TU app instalada.
//
// IMPORTANTE: el segmento `/apps/` lleva la API KEY / Client ID público (Partner
// Dashboard → Client ID / la misma que `SHOPIFY_API_KEY` en runtime), NO el
// handle `fengcart-app` de shopify.app.toml. Ejemplo válido:
//   https://admin.shopify.com/store/feng-test-4/apps/<CLIENT_ID_HEX>/app/pricing
//
// El installer incluye ese valor como query param `apiKey` cuando redirige al CMS.

const safeAtob = (value) => {
  try {
    return atob(value);
  } catch (error) {
    return null;
  }
};

const extractStoreDomainFromHost = (hostBase64) => {
  if (!hostBase64) {
    return null;
  }
  const decoded = safeAtob(hostBase64);
  if (!decoded) {
    return null;
  }
  // Expected format: "admin.shopify.com/store/{storeName}"
  const match = decoded.match(/\/store\/([^/?#]+)/);
  if (!match) {
    return null;
  }
  return `${match[1]}.myshopify.com`;
};

// Resolve the merchant's *.myshopify.com domain from the available info.
// Prefers the `shop` query param (already in the right format), falls back
// to decoding it out of the base64 `host` param.
const resolveShopDomain = ({ shop, host }) => {
  if (shop && /\.myshopify\.com$/.test(shop)) {
    return shop;
  }
  if (shop) {
    return `${String(shop).split(".")[0]}.myshopify.com`;
  }
  return extractStoreDomainFromHost(host);
};

// Construye la URL admin que abre /app/pricing dentro del mismo Admin.
// `navigateTopTo` (anchor target=_top) carga esa URL en el marco SUPERIOR —
// así no intentamos meter admin.shopify.com dentro del iframe del CMS (XFO).
//
// Requiere `apiKey` (query param desde el installer) + `shop` o `host` para el handle de tienda.
export const buildInstallerPricingUrl = ({ host, shop, apiKey }) => {
  const shopDomain = resolveShopDomain({ shop, host });
  if (!shopDomain || !apiKey) {
    return null;
  }

  const storeHandle = shopDomain.replace(".myshopify.com", "");
  return `https://admin.shopify.com/store/${storeHandle}/apps/${apiKey}/app/pricing`;
};

export const getQueryParam = (key) => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
  } catch (error) {
    return null;
  }
};

// Navigate the *top* window to the given URL, even from inside a cross-origin
// iframe (Shopify Admin embeds the CMS at admin.shopify.com).
//
// Why this is non-trivial:
//   - `window.location.href = url` only changes the iframe's URL. If the URL
//     is admin.shopify.com/..., Shopify replies with `X-Frame-Options: deny`
//     and the user sees a blank "refused to connect" page.
//   - `window.top.location.href = url` is blocked by browsers with a silent
//     SecurityError when the top frame is on a different origin (Shopify
//     Admin) than the iframe (FengCart CMS), even on user gestures.
//   - `window.open(url, "_top")` works in some browsers but is inconsistent.
//   - Shopify App Bridge `Redirect` is the "official" way but requires the
//     iframe to be a registered Shopify embedded app. The CMS isn't — it's
//     just an iframe served from dev-admin.fengcart.com — so initializing
//     App Bridge with the installer's apiKey throws `APP::ERROR::INVALID_ORIGIN`.
//
// The technique that actually works across all browsers from a cross-origin
// iframe is to programmatically click an `<a target="_top">` element. The
// browser treats this as a user-initiated top navigation (because the JS
// runs within the user-gesture stack of the merchant's click) and bypasses
// the cross-origin restrictions that block `window.top.location`.
export const navigateTopTo = (url) => {
  if (typeof window === "undefined" || !url) {
    return false;
  }

  if (window.top === window.self) {
    window.location.href = url;
    return true;
  }

  try {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_top";
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  } catch (error) {
    console.error("Top-frame navigation via anchor failed:", error);
  }

  try {
    if (window.open(url, "_top")) {
      return true;
    }
  } catch (_) {
    // ignore
  }

  try {
    window.top.location.href = url;
    return true;
  } catch (_) {
    // ignore
  }

  window.location.href = url;
  return true;
};

// TOP-navigate a admin.shopify.com/.../app/pricing con client id + store handle.
export const redirectToInstallerPricing = ({ host, shop, apiKey }) => {
  const url = buildInstallerPricingUrl({ host, shop, apiKey });
  if (!url) {
    return false;
  }
  return navigateTopTo(url);
};
